var _a;
import * as React from 'react';
import { login as sdkLogin, logout as sdkLogout } from '../../global';
import { onAuthStateChanged, onAuthLoadingChanged } from '../../global';
// Whether we're in a server-side rendering context (no hooks allowed).
// React Native has no `window` but DOES support hooks.
// We detect Node SSR specifically via process.versions.node to avoid
// false positives from Node 21+ which defines `navigator` globally.
const isSSR = typeof window === 'undefined'
    && typeof process !== 'undefined'
    && !!((_a = process.versions) === null || _a === void 0 ? void 0 : _a.node);
export function useAuth() {
    // Provide a fallback so server render doesn't break
    if (isSSR) {
        return {
            login: async (_options) => undefined,
            logout: async () => undefined,
            loading: true,
            user: null,
        };
    }
    const [user, setUser] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [sdkLoading, setSdkLoading] = React.useState(false);
    React.useEffect(() => {
        const stopAuth = onAuthStateChanged((user) => {
            setUser(user);
            setLoading(false);
        });
        const stopLoading = onAuthLoadingChanged((loading) => {
            setSdkLoading(loading);
        });
        return () => {
            stopAuth();
            stopLoading();
        };
    }, []);
    const login = async (options) => {
        try {
            setLoading(true);
            const user = await sdkLogin(options);
            setUser(user);
        }
        catch (error) {
            // Only log errors that aren't user-initiated cancellations
            if (error !== 'exited_auth_flow' && (error === null || error === void 0 ? void 0 : error.message) !== 'exited_auth_flow') {
                console.error('Error logging in:', error);
            }
            setUser(null);
        }
        finally {
            setLoading(false);
        }
    };
    const logout = async () => {
        try {
            setLoading(true);
            await sdkLogout();
            setUser(null);
        }
        catch (error) {
            console.error('Error logging out:', error);
        }
        finally {
            setLoading(false);
        }
    };
    return {
        login,
        logout,
        loading: loading || sdkLoading,
        user,
    };
}
